import type { UseQueryOptions } from "@tanstack/react-query";
import { skipToken, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import api from "../api";
import urls from "../urls";
import type { Person } from "../utils/backend-types";
import { personQueries } from "./usePerson";

type UsePersonsParams =
  | {
      variantId: number | undefined;
      siteId?: never;
      queryOptions?: Partial<UseQueryOptions<Array<Person> | undefined>>;
    }
  | {
      variantId?: never;
      siteId: number | undefined;
      queryOptions?: Partial<UseQueryOptions<Array<Person> | undefined>>;
    }
  | {
      variantId?: never;
      siteId?: never;
      queryOptions?: Partial<UseQueryOptions<Array<Person> | undefined>>;
    };

function usePersons({
  variantId,
  siteId,
  queryOptions = {}
}: UsePersonsParams) {
  const id = variantId || siteId;
  const { isLoading, error, data } = useQuery({
    queryKey: personQueries.list({ variantId, siteId }).queryKey,
    queryFn:
      typeof id !== "undefined"
        ? () => fetchPersons(variantId, siteId)
        : skipToken,
    enabled: typeof id !== "undefined",
    refetchInterval: false,
    refetchOnWindowFocus: false,
    ...queryOptions
  });

  const paragraph6Persons = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.filter((person) => person.isParagraph_6EegActive);
  }, [data]);

  return {
    isLoading: isLoading,
    error: error,
    persons: data || [],
    paragraph6Persons
  };
}

async function fetchPersons(
  variantId?: number | undefined,
  siteId?: number | undefined
) {
  const id = variantId || siteId;
  if (!id) {
    throw new Error("Missing id");
  }

  const response = await api.get<Array<Person>>(
    siteId ? urls.api.personsBySiteId(id) : urls.api.personsByVariantId(id)
  );
  return response.data;
}

export { usePersons };
